import React from 'react';
import classnames from 'classnames';
import * as rbd from 'react-beautiful-dnd';
import { Input } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import { Bubble } from '@home/components/TeaComponents';
import EllipsisHover from '@home/components/EllipsisHover';
import GameImage from '@home/components/GameImage';

import { getEntityTypeIcon } from '@home/pages/home/Explore/_config/common';
import { getUrlCollectionSystemDisplayInfo } from '../../../../utils/systemDisplay';
import {
  getShortcutDisplayCover,
  getShortcutPlatformTypes,
  getUrlCollectionItemKey,
  getUrlCollectionPrimaryEntityType,
} from '@home/utils/urlCollectionData';
import { EntityType } from '@home/enums/urlCollection';

import type { IUrlCollectionItem } from '@home/types/urlCollection';

import ICON_COMBINE from '@home/assets/svg/urlCollection/combine.svg';

import './index.scss';

interface IShortcutEditItemProps extends IBaseProps {
  item: IUrlCollectionItem;
  editingKey: string;
  editingName: string;
  nameErrorKey: string;
  nameErrorText: string;
  disabledAction?: boolean;
  isDragging?: boolean;
  draggableProps?: rbd.DraggableProvidedDraggableProps;
  dragHandleProps?: rbd.DraggableProvidedDragHandleProps | null;
  innerRef?: (element: HTMLDivElement | null) => void;
  renameTooltipText: string;
  deleteTooltipText: string;
  formatMessage: (descriptor: { id: string; defaultMessage?: string }) => string;
  setEditingName: (value: string) => void;
  setNameErrorKey: (value: string) => void;
  setNameErrorText: (value: string) => void;
  startRename: (item: IUrlCollectionItem) => void;
  finishRename: () => void;
  cancelRename: () => void;
  handleRemoveClick: (item: IUrlCollectionItem) => void;
  handleItemClick?: (item: IUrlCollectionItem) => void;
}

const ShortcutEditItem: React.FC<IShortcutEditItemProps> = (props) => {
  const {
    style,
    className,
    item,
    editingKey,
    editingName,
    nameErrorKey,
    nameErrorText,
    disabledAction = false,
    isDragging = false,
    draggableProps,
    dragHandleProps,
    innerRef,
    renameTooltipText,
    deleteTooltipText,
    formatMessage,
    setEditingName,
    setNameErrorKey,
    setNameErrorText,
    startRename,
    finishRename,
    cancelRename,
    handleRemoveClick,
    handleItemClick,
  } = props;
  const itemKey = getUrlCollectionItemKey(item);
  const itemTitle = item?.name;
  const { systemName, systemAvatarIcon } = getUrlCollectionSystemDisplayInfo({
    system: item?.system,
    formatMessage,
  });
  const displayImage = getShortcutDisplayCover(item, systemAvatarIcon);
  const platformTypes = getShortcutPlatformTypes(item);
  const entityType = getUrlCollectionPrimaryEntityType(item?.entity_type);
  const isEntityShortcut = !!entityType;
  const isEditing = editingKey === itemKey;
  const hasNameError = isEditing && (!editingName.trim() || nameErrorKey === itemKey);
  const isTitleClickable = typeof handleItemClick === 'function' && !isEditing && !disabledAction;
  const showEntityTypeIcon = !!platformTypes?.length;
  const showSystemName = !!systemName;
  const nameErrorPlaceholder = nameErrorText.replace(/[.。]$/, '');
  const itemStyle = {
    ...style,
    ...(draggableProps?.style || {}),
  };

  return (
    <div
      {...draggableProps}
      ref={innerRef}
      style={itemStyle}
      className={classnames('dbh-shortcut-edit-item', className, {
        'dbh-shortcut-edit-item--dragging': isDragging,
        'dbh-shortcut-edit-item--disabled': disabledAction,
      })}
    >
      <span {...dragHandleProps} className="dbh-shortcut-edit-item__drag"></span>
      <div className="dbh-shortcut-edit-item__shortcut">
        <div className="dbh-shortcut-edit-item__thumb">
          <GameImage
            className="dbh-shortcut-edit-item__thumb-cover"
            src={displayImage}
            alt={itemTitle}
            entityType={isEntityShortcut ? entityType : EntityType.Mobile}
          />
        </div>
        <div className="dbh-shortcut-edit-item__content">
          {isEditing ? (
            <div
              className={classnames('dbh-shortcut-edit-item__title-edit-wrap', {
                'dbh-shortcut-edit-item__title-edit-wrap--error': hasNameError,
              })}
            >
              <Input
                autoFocus
                size="full"
                value={editingName}
                placeholder={hasNameError ? nameErrorPlaceholder : ''}
                onChange={(value) => {
                  setEditingName(value);
                  if (nameErrorKey) {
                    setNameErrorKey('');
                    setNameErrorText('');
                  }
                }}
                onBlur={finishRename}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Enter') {
                    finishRename();
                    return;
                  }

                  if (event.key === 'Escape') {
                    event.preventDefault();
                    event.stopPropagation();
                    cancelRename();
                  }
                }}
              />
              {hasNameError && !!editingName.trim() && (
                <div className="dbh-shortcut-edit-item__title-error">{nameErrorText}</div>
              )}
            </div>
          ) : (
            <div
              className={classnames('dbh-shortcut-edit-item__title-trigger', {
                'dbh-shortcut-edit-item__title-trigger--clickable': isTitleClickable,
              })}
              onClick={(event) => {
                if (!isTitleClickable || !handleItemClick) {
                  return;
                }

                event.stopPropagation();
                handleItemClick(item);
              }}
            >
              <EllipsisHover
                className={classnames('dbh-shortcut-edit-item__title', {
                  'dbh-shortcut-edit-item__title--clickable': isTitleClickable,
                })}
                placement="top"
                text={itemTitle}
              />
            </div>
          )}
          <div className="dbh-shortcut-edit-item__footer">
            {showEntityTypeIcon && (
              <>
                {platformTypes?.map((platformType) => {
                  const platformIcon = getEntityTypeIcon(platformType);

                  if (!platformIcon) {
                    return null;
                  }

                  if (platformType === EntityType.Combine) {
                    return (
                      <img
                        className="dbh-shortcut-edit-item__entity-icon"
                        key={platformType}
                        src={ICON_COMBINE}
                        alt=""
                      />
                    );
                  }

                  return (
                    <SvgIcon className="dbh-shortcut-edit-item__entity-icon" key={platformType} type={platformIcon} />
                  );
                })}
              </>
            )}
            {showEntityTypeIcon && showSystemName && <span className="dbh-shortcut-edit-item__divider" />}
            <span className="dbh-shortcut-edit-item__system-name">{systemName}</span>
          </div>
        </div>
      </div>
      <div className="dbh-shortcut-edit-item__actions">
        {isEditing || (
          <Bubble content={renameTooltipText}>
            <span>
              <SvgIcon
                className="dbh-shortcut-edit-item__action-icon dbh-shortcut-edit-item__action-icon-rename"
                type="pencil"
                onClick={(event) => {
                  if (disabledAction) {
                    return;
                  }

                  event.stopPropagation();
                  startRename(item);
                }}
              />
            </span>
          </Bubble>
        )}
        <Bubble content={deleteTooltipText}>
          <span
            className="dbh-shortcut-edit-item__action-icon dbh-shortcut-edit-item__action-icon-dismiss"
            onClick={(event) => {
              if (disabledAction) {
                return;
              }

              event.stopPropagation();
              handleRemoveClick(item);
            }}
          ></span>
        </Bubble>
      </div>
    </div>
  );
};

export default ShortcutEditItem;
